import React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { ResponsiveModal, useGlobalModalStack } from "@wtc/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ShareViewModal } from "@/components/shared/SavedViewsControls/components/ShareViewModal";
import { useSavedViewsModal } from "./contexts/SavedViewsModalContext";
import { validateUserAccess } from "./utils";

/**
 * SavedViewsModals - Renders Save As, Share, and Delete modals
 * Uses SavedViewsModalContext to access view state and mutations
 * Eliminates prop drilling by using context instead of props
 */
export const SavedViewsModals: React.FC = () => {
  const {
    selectedView,
    resource,
    entityName,
    shareOpen,
    setShareOpen,
    shareVisibility,
    setShareVisibility,
    shareUserIds,
    setShareUserIds,
    createViewMutation,
    updateViewMutation,
    deleteOpen,
    setDeleteOpen,
    deleteViewMutation,
    serializeFilters,
    saveAsName: contextSaveAsName,
    setSaveAsName: contextSetSaveAsName,
    onShare,
    onDelete,
  } = useSavedViewsModal();

  const { closeModal } = useGlobalModalStack();

  // Save As modal state (local to this component)
  const [saveAsName, setSaveAsName] = React.useState("");
  const [saveAsLoading, setSaveAsLoading] = React.useState(false);
  const [saveAsError, setSaveAsError] = React.useState<Error | null>(null);

  // Sync local state with context if provided
  React.useEffect(() => {
    if (contextSaveAsName !== undefined) {
      setSaveAsName(contextSaveAsName);
    }
  }, [contextSaveAsName]);

  React.useEffect(() => {
    if (contextSetSaveAsName) {
      contextSetSaveAsName(saveAsName);
    }
  }, [saveAsName, contextSetSaveAsName]);

  const handleSaveAs = async () => {
    if (!saveAsName.trim()) {
      setSaveAsError(new Error("View name is required"));
      return;
    }

    if (!serializeFilters) {
      setSaveAsError(new Error("Unable to serialize filters"));
      return;
    }

    setSaveAsLoading(true);
    setSaveAsError(null);

    try {
      const filters = serializeFilters();

      await createViewMutation.mutateAsync({
        resource,
        name: saveAsName.trim(),
        filters,
        visibility: "private",
      });

      // Success - close modal and reset
      setSaveAsName("");
      closeModal("tabs-save-view-as");
    } catch (error) {
      setSaveAsError(error instanceof Error ? error : new Error("Failed to save view"));
    } finally {
      setSaveAsLoading(false);
    }
  };

  return (
    <>
      {/* Save View As Modal */}
      <ResponsiveModal
        globalModalId="tabs-save-view-as"
        title="Save View"
        description="Name this view and save the current filters."
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label htmlFor="view-name" className="text-sm font-semibold">
                View Name
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Give this view a descriptive name
              </p>
            </div>
            <Input
              id="view-name"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              placeholder="e.g. Open VIP Tickets"
              disabled={saveAsLoading}
              className="text-sm"
            />
          </div>

          {saveAsError && (
            <Alert variant="destructive" className="border-l-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {saveAsError instanceof Error ? saveAsError.message : "Failed to save view"}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex w-full justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => closeModal("tabs-save-view-as")}
              disabled={saveAsLoading}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAs}
              disabled={!saveAsName.trim() || saveAsLoading}
              aria-busy={saveAsLoading}
              className="text-sm"
            >
              {saveAsLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Share View Modal - Uses context for view data */}
      <ShareViewModal
        globalModalId="tabs-share-view"
        selectedView={selectedView}
        resource={resource}
        shareVisibility={shareVisibility}
        onShareVisibilityChange={setShareVisibility}
        shareUserIds={shareUserIds}
        onShareUserIdsChange={setShareUserIds}
        updateView={updateViewMutation}
        entityName={entityName}
        validateUserAccess={validateUserAccess}
        onSave={onShare}
      />

      {/* Delete View Modal */}
      <ResponsiveModal
        globalModalId="tabs-delete-view"
        title="Delete View"
        description="This will permanently delete this view."
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. All users with access to this view will lose access.
            </p>
          </div>

          {deleteViewMutation.error && (
            <Alert variant="destructive" className="border-l-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {deleteViewMutation.error instanceof Error
                  ? deleteViewMutation.error.message
                  : "Failed to delete view"}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => closeModal("tabs-delete-view")}
              disabled={deleteViewMutation.isPending}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={deleteViewMutation.isPending}
              aria-busy={deleteViewMutation.isPending}
              className="text-sm"
            >
              {deleteViewMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};
